/**
 * Billing Page Template
 *
 * Pure presentation component for billing/subscription page
 * Displays current plan, usage/credits, and available plans for upgrade
 *
 * REFACTORED with Facade Pattern!
 *
 * Before: 15+ individual props (user, sidebar, plan data, handlers...)
 * After: 1 facade + 2 platform-specific props
 *
 * @package @ideoz/ui
 */

import React from 'react';
import { Check, WandSparkles, Eye } from 'lucide-react';
import { Button } from '../atoms/button';
import { AppLayout } from '../layouts/AppLayout';
import { cn, Skeleton } from '@ideoz/ui';
import type { BillingPageFacade } from '@ideoz/core';
import Icon from '../molecules/Icon';
import UpgradeToSmartDialog from '../molecules/UpgradeToSmartDialog';
import CreditExhaustionModal from '../molecules/CreditExhaustionModal';
import DowngradeConfirmationModal from '../molecules/DowngradeConfirmationModal';
import CancelPendingDowngradeModal from '../molecules/CancelPendingDowngradeModal';
import PendingDowngradeBanner from '../molecules/PendingDowngradeBanner';

/**
 * BillingPageTemplate Props
 *
 * SIMPLIFIED with Facade Pattern!
 *
 * Before: 15+ individual props
 * After: 1 facade + 2-3 platform-specific props
 *
 * The facade encapsulates all shared logic following useMyProfilePageFacade pattern:
 * - State (isLoggedIn, user)
 * - Header configuration & user menu
 * - Navigation
 * - Billing data & actions
 */
export interface BillingPageTemplateProps {
  // ========== FACADE (all shared logic) ==========
  facade: BillingPageFacade;

  // ========== Platform-Specific Props ==========
  isWeb: boolean;

  // Web-specific
  authDialogState?: {
    isOpen: boolean;
    mode: 'login' | 'register';
    openLogin: () => void;
    openRegister: () => void;
    closeDialog: () => void;
    isLoading: boolean;
  };
}

export default function BillingPageTemplate({
  facade,
  isWeb,
  authDialogState,
}: BillingPageTemplateProps): JSX.Element {
  // ========== Destructure Facade ==========
  const {
    state: { user, hasUpgradedPlan },
    header: {
      headerProps,
      userMenuOpen,
      onUserMenuClose,
      userMenuItems,
      userMenuSocialLinks,
      onRequestUpgrade,
      isRequestingUpgrade,
    },
    navigation,
    actions: { onUpgrade, onConfirmUpgrade, onCancelUpgrade, onDowngrade, onConfirmDowngrade, onCancelDowngrade, onCancelPendingDowngrade, onConfirmCancelPendingDowngrade, onCloseCancelPendingDowngradeModal, onRenewSubscription, onDownloadInvoice, onUpdatePaymentMethod },
    upgradeDialog: { isOpen: upgradeDialogOpen, isLoading: isUpgrading },
    downgradeDialog: { isOpen: downgradeDialogOpen, isLoading: isDowngrading },
    pendingDowngrade: { isPending: isPendingDowngrade, effectiveDate: pendingDowngradeDate, isCancelling: isCancellingDowngrade },
    cancelPendingDowngradeModal: { isOpen: cancelPendingDowngradeModalOpen, isLoading: isCancellingPendingDowngrade },
    creditExhaustionModal: { isOpen: creditExhaustionModalOpen, onClose: onCloseCreditExhaustionModal, availableCredits, totalCredits },
    subscriptionExpiration: { isExpiringWithin3Days, daysUntilExpiration },
    data: { currentPlan, creditBalances, monthlyCredits, purchasedCredits, nextResetDate, availablePlans, invoices, isLoadingPlans, isLoadingInvoices, paymentMethod, isLoadingPaymentMethod, billingCycle },
  } = facade;

  const primaryBalance = creditBalances?.[0];
  const currentPlanData = availablePlans.find((plan) => plan.isCurrent);
  const currentPlanId = currentPlanData?.id;
  const isFreePlan = currentPlan.name === 'Free' || currentPlanData?.name === 'Free';
  // Find Smart plan by name (since IDs are now UUIDs)
  const upgradeTargetPlan = availablePlans.find((plan) => plan.name === 'Smart')
    || availablePlans.find((plan) => !plan.isCurrent)
    || {
    id: currentPlanId,
    name: currentPlan.name,
    price: currentPlan.price,
    billingPeriod: currentPlan.billingPeriod,
  };
  const today = new Date();
  const upgradeEffectiveDate = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()} Immediately`;

  // Guard against null user
  if (!user) {
    return (
      <div className="p-6 space-y-4" data-testid="billing-loading">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-72" />
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  return (
    <AppLayout
      currentPath="/billing"
      sidebar={facade.sidebar}
      user={{
        isLoggedin: !!user,
        name: user?.name,
        avatar: user?.avatar,
      }}
      userMenuProps={
        user
          ? {
            points: headerProps.points,
            pointsLabel: 'Ideoz AI Points',
            menuItems: userMenuItems || [],
            socialLinks: userMenuSocialLinks || [],
            onRequestUpgrade,
            isRequestingUpgrade: isRequestingUpgrade || false,
            canRequestUpgrade: (headerProps.points || 0) <= 0,
            isWeb,
          }
          : undefined
      }
      subscriptionExpirationBanner={
        isExpiringWithin3Days
          ? {
            isVisible: true,
            daysUntilExpiration,
            onRenew: onRenewSubscription,
          }
          : undefined
      }
      isWeb={isWeb}
      testId="billing-page"
      className="data-testid-billing-page"
    >
      <div className={`max-w-landing-page mx-auto py-16 flex-1 overflow-y-auto ${isWeb ? 'flex flex-col min-h-screen' : ''}`}
      >
        {/* Page Title */}
        <header className="mb-6 flex flex-col gap-2">
          <h2 className="text-2xl text-start font-semibold text-gray-800">Billing</h2>
          <p className="text-gray-600 text-sm">Manage your subscription and billing</p>
        </header>

        {/* Pending Downgrade Banner */}
        {isPendingDowngrade && pendingDowngradeDate && (
          <PendingDowngradeBanner
            isVisible={isPendingDowngrade}
            effectiveDate={pendingDowngradeDate}
            onCancelDowngrade={onCancelPendingDowngrade}
            isLoading={isCancellingDowngrade}
            testId="pending-downgrade-banner"
          />
        )}

        {/* Current Plan */}
        <section className="bg-white rounded-2xl p-6 mt-5" data-testid="current-plan-section">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="">
              <p className="text-sm font-semibold text-[#252B37] mb-2">Current plan</p>
              <p className="text-xs text-[#252B37] mb-4">Your active subscription</p>
              <p className="text-xs text-[#252B37] mb-0">Current plan</p>
              <p className="text-lg font-semibold text-[#4B579F] mb-0">{currentPlan.name}</p>
              <p className="text-xs text-[#252B37] mb-0">${currentPlan.price}/{currentPlan.billingPeriod}</p>
            </div>
            <a href="#plans-section" className="bg-white text-gray-600 border border-[#d5d7da] rounded-full hover:bg-[#fafafa] hover:text-[#252b37] focus-visible:ring-[#d5d7da] disabled:bg-white disabled:border-[#e9eaeb] disabled:text-[#a4a7ae] h-9 px-4 py-1 items-center justify-center flex text-sm">
              View plans
            </a>
          </div>
        </section>

        {/* Usage & Credits */}
        <section className="bg-white rounded-2xl p-6 mt-4 mb-4" data-testid="usage-credits-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm font-semibold text-[#252B37]">Usage & Credits</p>
              <p className="text-xs text-[#252B37] mb-4">Track your credit usage</p>
              <p className="text-lg font-semibold text-[#4B579F]"> {Math.max(0, (primaryBalance?.total ?? 0) - (primaryBalance?.used ?? 0))} Credits</p>
            </div>
          </div>

          {primaryBalance && (
            <div className="flex flex-col items-center justify-between mb-6">
              <div className="mb-2 w-full flex items-center justify-between">
                <p className="text-xs text-[#252B37]">Available credits</p>
                <p className="text-sm text-gray-600">
                  {Math.max(0, (primaryBalance?.total ?? 0) - (primaryBalance?.used ?? 0))}/{primaryBalance?.total ?? 0}
                </p>
              </div>
              <div className="w-full">
                <div className="w-full bg-[#E9EAEB] rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-blue-light-600 transition-all duration-300"
                    style={{ width: `${primaryBalance?.percentage ?? 0}%` }}
                    data-testid="credit-bar-available"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg py-2 px-4">
              <p className="text-xs text-[#717680]">Monthly Credits</p>
              <p className="text-lg font-semibold text-[#4B579F]">{monthlyCredits}</p>
            </div>
            <div className="border border-gray-200 rounded-lg py-2 px-4">
              <p className="text-xs text-[#717680]">Next reset:</p>
              <p className="text-lg font-semibold text-[#4B579F]">{nextResetDate}</p>
            </div>
          </div>

          
        </section>

        {/* Free plan note */}
        {isFreePlan && (
          <section className="rounded-xl border border-blue-light-600 bg-blue-light-50 p-4 my-4" data-testid="plan-note">
            <p className="text-xs font-semibold text-[#414651] mb-1">
              Free plan note:
            </p>
            <p className="text-xs text-[#535862]">
              Your 40 monthly credits are allocated to generate only 1 prototype per month. <span className="font-bold">Upgrade to Smart for 250 flexible credits</span> you can use across all features.
            </p>
          </section>
        )}

        {/* Plans */}
        <section id="plans-section" data-testid="available-plans-section" className="bg-white rounded-2xl p-6">
          <div className="space-y-1">
            <h2 className="text-sm font-semibold text-[#252B37]">Plans</h2>
            <p className="text-xs text-[#252B37]">Choose the plan that fits your needs.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {isLoadingPlans ? (
              // Loading skeleton for plans (2 skeleton cards)
              Array.from({ length: 2 }).map((_, index) => (
                <div
                  key={`plan-skeleton-${index}`}
                  className="rounded-lg border border-gray-200 bg-white p-4"
                  data-testid={`plan-skeleton-${index}`}
                >
                  {/* Header skeleton */}
                  <div className="flex items-start justify-between mb-2">
                    <Skeleton className="h-7 w-20" /> {/* Plan name */}
                    <Skeleton className="h-6 w-24 rounded-full" /> {/* Badge */}
                  </div>

                  {/* Description skeleton */}
                  <div className="space-y-2 mb-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-3/4" />
                  </div>

                  {/* Price skeleton */}
                  <Skeleton className="h-8 w-24 mb-3" />

                  {/* Features skeleton */}
                  <div className="space-y-3 mb-6">
                    {Array.from({ length: 6 }).map((_, featureIndex) => (
                      <div key={`feature-skeleton-${featureIndex}`} className="flex items-start gap-2">
                        <Skeleton className="h-4 w-4 rounded mt-0.5" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                    ))}
                  </div>

                  {/* Button skeleton */}
                  <Skeleton className="h-9 w-full rounded-lg" />
                </div>
              ))
            ) : (
              availablePlans.map((plan) => (
                <div
                  key={plan.id}
                  className={cn(
                    'rounded-lg border border-gray-200 bg-white p-4',
                    plan.isCurrent ? 'ring-1 ring-[#4B579F]' : ''
                  )}
                  data-testid={`plan-card-${plan.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                    </div>
                       {plan.recommended && !plan.isCurrent && (
                      <span className="flex gap-1 items-center text-xs font-medium text-[#067647] bg-success-50 px-2 py-1 rounded-full border border-[#ABEFC6]">
                        <Icon icon={WandSparkles} size={12} stroke='#17B26A' />
                        Recommended
                      </span>
                    )}
                    {plan.isCurrent && (
                      <span className="text-xs font-medium text-[#414651] bg-[#FAFAFA] px-2 py-1 rounded-full border border-gray-200">
                        Current
                      </span>
                    )}
                  </div>

                  <div className="text-sm text-gray-600 mb-2">
                    <p className="mb-1 text-[#414651] text-sm font-medium">{plan.description}</p>
                    <p className="text-[#717680] text-xs">{plan.subDescription}</p>
                  </div>

                  <p className="text-3xl font-semibold text-[#4B579F] mb-3">
                    ${plan.price}
                    <span className="text-xs font-normal text-[#717680]">/{plan.billingPeriod}</span>
                  </p>

                  <div className="space-y-3 mb-6">
                    {plan.features.map((feature) => (
                      <div key={feature.id} className="flex items-start gap-2" data-testid={`feature-${feature.id}`}>
                        <Check size={18} className="text-green-500 mt-0.5" />
                        <span className="text-sm text-[#717680]">{feature.name}</span>
                      </div>
                    ))}
                  </div>

                  {plan.isCurrent ? (
                    <Button variant="secondary-gray" disabled className="w-full" data-testid={`plan-button-${plan.id}`}>
                      Current
                    </Button>
                  ) : plan.name === 'Free' && hasUpgradedPlan ? (
                    <Button
                      onClick={() => onDowngrade?.()}
                      disabled={isPendingDowngrade}
                      variant="secondary-gray"
                      className="w-full"
                      data-testid={`plan-button-${plan.id}`}
                    >
                      {isPendingDowngrade ? 'Downgrade Pending' : 'Downgrade'}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={() => onUpgrade?.(plan.id)}
                      className="w-full"
                      data-testid={`plan-button-${plan.id}`}
                    >
                      Upgrade
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </section>

        {/* Only show for upgraded users */}
        {hasUpgradedPlan && (
          <>

            {/* Billing Cycle */}
            {billingCycle && (
              <section className="bg-white rounded-2xl p-6 mt-6" data-testid="billing-cycle-section">
                <h3 className="text-sm font-semibold text-[#252B37] mb-4">Billing Cycle</h3>
                <p className="text-xs text-[#717680] mb-4">Manage your subscription renewal</p>
                <div className="border border-gray-200 rounded-lg py-2 px-4 mb-2">
                  <p className="text-xs text-[#717680]">Billing period</p>
                  <p className="text-xs font-medium text-[#414651]">{billingCycle.billingPeriod}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg py-2 px-4">
                    <p className="text-xs text-[#717680] mb-1">Next billing date</p>
                    <p className="text-sm font-semibold text-[#4B579F]">{billingCycle.nextBillingDate}</p>
                  </div>
                  <div className="border border-gray-200 rounded-lg py-2 px-4">
                    <p className="text-xs text-[#717680] mb-1">Next charge</p>
                    <p className="text-sm font-semibold text-[#4B579F]">{billingCycle.nextCharge}</p>
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {/* Payment Method - Show when loading or when payment method exists */}
        {(isLoadingPaymentMethod || paymentMethod) && (
          <section className="bg-white rounded-2xl p-6 mt-6" data-testid="payment-method-section">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-sm font-semibold text-[#252B37] mb-1">Payment method</h3>
                <p className="text-xs text-[#717680]">Manage your payment information</p>
              </div>
            </div>

            {isLoadingPaymentMethod ? (
              <div className="flex items-center gap-4 border border-gray-200 rounded-lg py-2 px-4 mb-2">
                <Skeleton className="w-12 h-8 rounded" />
                <div>
                  <Skeleton className="h-4 w-32 mb-1" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="ml-auto h-9 w-20 rounded-lg" />
              </div>
            ) : paymentMethod ? (
              <div className="flex items-center gap-4 border border-gray-200 rounded-lg py-2 px-4 mb-2">
                <img
                  src={`https://s3.us-east-1.amazonaws.com/app-test.ideoz.ai/credit+card+brand+logos/${paymentMethod.brand}.png`}
                  alt={paymentMethod.brand}
                  className="w-12 h-8 object-contain rounded"
                />
                <div>
                  <p className="text-xs font-medium text-[#414651]">{paymentMethod.maskedNumber}</p>
                  <p className="text-xs text-[#717680]">Expires {paymentMethod.expiryDate}</p>
                </div>
                <Button
                  variant="outline"
                  className="ml-auto rounded-lg border border-gray-300 text-[#414651] font-semibold h-9 px-3"
                  onClick={onUpdatePaymentMethod}
                >
                  Update
                </Button>
              </div>
            ) : null}
          </section>
        )}




        {/* Invoice History */}
        <section className="bg-white rounded-2xl p-6 mt-6" data-testid="invoice-history-section">
          <h3 className="text-sm font-semibold text-[#252B37]">Invoices</h3>
          <p className="text-xs text-[#717680] mb-6">Download your billing history</p>
          <div className="space-y-2">
            {isLoadingInvoices ? (
              // Loading skeleton for invoices
              Array.from({ length: 2 }).map((_, index) => (
                <div
                  key={`invoice-skeleton-${index}`}
                  className="flex items-center justify-between border border-gray-200 rounded-lg py-2 px-4"
                >
                  <div>
                    <Skeleton className="h-3 w-24 mb-1" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-6 w-16 rounded-full" />
                    <Skeleton className="h-5 w-5 rounded" />
                  </div>
                </div>
              ))
            ) : invoices && invoices.length > 0 ? (
              invoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between border border-gray-200 rounded-lg py-2 px-4">
                  <div>
                    <p className="text-xs text-[#717680]">{invoice.date}</p>
                    <p className="text-xs font-medium text-[#414651]">{invoice.amount}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={cn(
                      "flex gap-1 items-center text-xs font-medium px-2 py-1 rounded-full border",
                      invoice.displayStatus === 'paid'
                        ? "text-[#067647] bg-success-50 border-[#ABEFC6]"
                        : invoice.displayStatus === 'pending'
                          ? "text-[#B54708] bg-warning-50 border-[#FEDF89]"
                          : "text-[#B42318] bg-error-50 border-[#FECDCA]"
                    )}>
                      {invoice.displayStatus === 'paid' ? 'Paid' : invoice.displayStatus === 'pending' ? 'Pending' : 'Failed'}
                    </span>
                    <Button
                      variant="ghost"
                      className="text-[#717680] hover:text-[#414651] p-0 h-auto"
                      onClick={() => onDownloadInvoice(invoice.id)}
                      data-testid={`download-invoice-${invoice.id}`}
                    >
                      <Icon icon={Eye} size={20} />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-[#717680] text-center py-4">No invoices available</p>
            )}
          </div>
        </section>




      </div>

      {/* Upgrade to Smart Dialog */}
      <UpgradeToSmartDialog
        isOpen={upgradeDialogOpen}
        isLoading={isUpgrading}
        onClose={onCancelUpgrade}
        onConfirm={onConfirmUpgrade}
        currentPlan={{
          id: currentPlanId,
          name: currentPlan.name,
          price: currentPlan.price,
          billingPeriod: currentPlan.billingPeriod,
        }}
        newPlan={{
          id: upgradeTargetPlan.id,
          name: upgradeTargetPlan.name,
          price: upgradeTargetPlan.price,
          billingPeriod: upgradeTargetPlan.billingPeriod,
        }}
        effectiveDate={upgradeEffectiveDate}
      />

      {/* Downgrade Confirmation Modal */}
      <DowngradeConfirmationModal
        isOpen={downgradeDialogOpen}
        isLoading={isDowngrading}
        onClose={onCancelDowngrade}
        onConfirm={onConfirmDowngrade}
        currentPlanName={currentPlan.name}
        currentPlanPrice={currentPlan.price}
        currentPlanCredits={monthlyCredits}
        nextBillingDate={billingCycle?.nextBillingDate || 'N/A'}
      />

      {/* Cancel Pending Downgrade Modal */}
      <CancelPendingDowngradeModal
        isOpen={cancelPendingDowngradeModalOpen}
        isLoading={isCancellingPendingDowngrade}
        onClose={onCloseCancelPendingDowngradeModal}
        onConfirm={onConfirmCancelPendingDowngrade}
      />

      {/* Credit Exhaustion Modal */}
      <CreditExhaustionModal
        isOpen={creditExhaustionModalOpen}
        onClose={onCloseCreditExhaustionModal}
        availableCredits={availableCredits}
        totalCredits={totalCredits}
        onUpgrade={onConfirmUpgrade}
        isLoading={isUpgrading}
        testId="credit-exhaustion-modal"
      />
    </AppLayout>
  );
}
